import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { Download, Loader2, Pencil, Plus, Power, RefreshCw, Tag, Upload, X } from 'lucide-react';
import * as XLSX from 'xlsx';
import { PageHeader } from '../../../../components/ui/PageHeader';
import { FilterBar } from '../../../../components/ui/FilterBar';
import { SearchInput } from '../../../../components/ui/SearchInput';
import { FilterableSelect } from '../../../../components/ui/FilterableSelect';
import { Switch } from '../../../../components/ui/Switch';
import { DataTable } from '../../../../components/ui/DataTable';
import { Button } from '../../../../components/ui/Button';
import { ToneBadge } from '../../../../components/ui/ToneBadge';
import { ConfirmDialog } from '../../../../components/ui/ConfirmDialog';
import { useNotice } from '../../../../hooks/useNotice';
import { ProductFlags } from './components/ProductFlags';
import { ProductFormModal } from './modals/ProductFormModal';
import { CatalogUploadModal } from './modals/CatalogUploadModal';
import { ShopifySkuSyncModal } from './modals/ShopifySkuSyncModal';
import { fetchCatalog, updateProduct } from '../../../../features/catalog/catalogThunks';
import { fetchStockSyncChannels } from '../../../../features/stockSync/stockSyncThunks';
import {
  stockSyncChannelSelected,
  stockSyncLeft,
  stockSyncWatched,
} from '../../../../features/stockSync/stockSyncSlice';
import { selectIsRunActive, selectUsableChannels } from '../../../../features/stockSync/stockSyncSelectors';
import { RUN_KIND, STOCK_SYNC_WATCHER } from '../../../../features/stockSync/stockSyncConstants';
import { pickDefaultChannelId } from '../../../../features/stockSync/stockSyncUtils';
import { setCatalogFilters } from '../../../../features/catalog/catalogSlice';
import { ALL_CLIENTS } from '../../../../features/catalog/catalogConstants';
import {
  buildCatalogExportRows,
  catalogExportFileName,
} from '../../../../features/catalog/catalogUtils';
import {
  selectAllClientOptions,
  selectCurrentWarehouse,
} from '../../../../features/warehouseContext/warehouseContextSlice';
import { formatQuantity } from '../../../../utils/inventoryLabels';

/** The button stays usable while a sync runs: it opens the dialog that follows it. */
const SyncingIcon = ({ className, ...props }) => <Loader2 {...props} className={clsx(className, 'animate-spin')} />;

/**
 * `/warehouse/products`: the product master of the clients.
 *
 * The catalog belongs to whoever owns the merchandise, and it is the same one
 * in every centre that company stores in — so the warehouse in use does not
 * narrow it; it only decides which stock the rows show. The server decides who may maintain whose: a client out of reach
 * answers 404, one that cannot be maintained answers 403, and nothing here
 * works that out.
 *
 * Nothing is deleted. A product with history would leave movements of somebody
 * else's goods pointing at a SKU that no longer exists; it is taken out of
 * service, which keeps the trail and the stock.
 */
export const Products = () => {
  const dispatch = useDispatch();

  const { products, totalCount, loading, error, filters, busyId } = useSelector(
    (state) => state.catalog
  );
  const clientOptions = useSelector(selectAllClientOptions);
  const warehouse = useSelector(selectCurrentWarehouse);
  const shopifyChannels = useSelector(selectUsableChannels);
  const shopifySelectedId = useSelector((state) => state.stockSync.selectedId);
  const catalogSyncRunning = useSelector(selectIsRunActive(RUN_KIND.CATALOG));

  const [searchInput, setSearchInput] = useState(filters.search);
  /** `null` closed, `{ product: null }` a new one, `{ product }` an edit. */
  const [form, setForm] = useState(null);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isShopifyOpen, setIsShopifyOpen] = useState(false);
  const [toRetire, setToRetire] = useState(null);
  const setNotice = useNotice('products-notice');

  // The catalog is the company's in every warehouse, and so are the stores that feed it.
  useEffect(() => {
    dispatch(stockSyncWatched(STOCK_SYNC_WATCHER.PRODUCTS));
    dispatch(fetchStockSyncChannels());
    return () => dispatch(stockSyncLeft(STOCK_SYNC_WATCHER.PRODUCTS));
  }, [dispatch]);

  // Debounce for search
  useEffect(() => {
    const handler = setTimeout(() => {
      const search = searchInput.trim();
      if (search !== filters.search) dispatch(setCatalogFilters({ search }));
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput, filters.search, dispatch]);

  // Fetch when relevant filters change
  useEffect(() => {
    dispatch(fetchCatalog());
  }, [
    dispatch,
    warehouse?.id,
    filters.search,
    filters.company,
    filters.onlyActive,
    filters.onlyWithStock,
    filters.page,
    filters.rowsPerPage,
  ]);


  // Somebody who only has one client needs neither a filter nor a column
  // repeating its name.
  const showClient = clientOptions.length > 1;
  const canCreate = clientOptions.length > 0;
  const filteredCompanyId = filters.company === ALL_CLIENTS ? null : filters.company;
  const filteredClient = clientOptions.find((option) => option.value === filteredCompanyId);

  const toggleActive = async (product) => {
    const action = await dispatch(
      updateProduct({ productId: product.id, values: { is_active: !product.is_active } })
    );
    setToRetire(null);
    setNotice(
      action.meta.requestStatus === 'fulfilled'
        ? {
            type: 'success',
            message: product.is_active
              ? `${product.sku} quedó de baja. Su historial y su stock siguen ahí.`
              : `${product.sku} está activo de nuevo.`,
          }
        : { type: 'error', message: action.payload }
    );
  };

  // Taking a product out of service does not take its goods out of the
  // warehouse, and whoever does it should know they are still there.
  const handleToggleClick = (product) => {
    if (product.is_active && Number(product.stock_total) > 0) setToRetire(product);
    else toggleActive(product);
  };

  const openShopifySync = () => {
    dispatch(
      stockSyncChannelSelected(
        pickDefaultChannelId(shopifyChannels, { selectedId: shopifySelectedId, companyId: filteredCompanyId })
      )
    );
    setIsShopifyOpen(true);
  };

  const handleExport = () => {
    const sheet = XLSX.utils.json_to_sheet(buildCatalogExportRows(products));
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Productos');
    XLSX.writeFile(book, catalogExportFileName(filteredClient?.label));
  };

  const columns = [
    ...(showClient
      ? [
          {
            header: 'CLIENTE',
            cell: (row) => <span className="text-text-secondary">{row.company_name}</span>,
          },
        ]
      : []),
    {
      // SKU, name and barcode are the same thing looked at in three ways.
      header: 'PRODUCTO',
      cell: (row) => (
        <div className="flex flex-col max-w-[340px]">
          <span className="font-bold font-mono text-ninja-dark dark:text-white">{row.sku}</span>
          <span className="text-xs text-text-muted mt-0.5 truncate" title={row.name}>
            {row.name}
          </span>
          {row.barcode && (
            <span className="text-xs text-text-muted font-mono mt-0.5">EAN {row.barcode}</span>
          )}
        </div>
      ),
    },
    { header: 'CONTROL', cell: (row) => <ProductFlags product={row} /> },
    {
      header: 'ESTADO',
      cell: (row) => (
        <ToneBadge tone={row.is_active ? 'success' : 'neutral'} testId="product-status">
          {row.is_active ? 'Activo' : 'De baja'}
        </ToneBadge>
      ),
    },
    {
      header: 'ACCIONES',
      cell: (row) => {
        const isBusy = busyId === row.id;
        return (
          <div className="flex items-center justify-end gap-1">
            <Button
              variant="subtle"
              size="sm"
              iconOnly
              icon={Pencil}
              title="Editar"
              aria-label={`Editar ${row.sku}`}
              data-testid="product-edit"
              disabled={isBusy}
              onClick={() => setForm({ product: row })}
            />
            <Button
              variant="subtle"
              size="sm"
              iconOnly
              tone={row.is_active ? 'danger' : 'success'}
              icon={row.is_active ? X : Power}
              loading={isBusy}
              title={
                row.is_active
                  ? 'Dar de baja: conserva el historial y deja de ofrecerse para cosas nuevas'
                  : 'Reactivar'
              }
              aria-label={`${row.is_active ? 'Dar de baja' : 'Reactivar'} ${row.sku}`}
              data-testid="product-toggle-active"
              onClick={() => handleToggleClick(row)}
            />
          </div>
        );
      },
    },
  ];

  // An empty table has to say why: empty looks the same as broken.
  const emptyMessage = (() => {
    if (filters.onlyWithStock) {
      return warehouse
        ? `Ningún producto tiene stock en ${warehouse.name}. Apaga el filtro para ver el catálogo completo.`
        : 'Ningún producto tiene stock. Apaga el filtro para ver el catálogo completo.';
    }
    if (filters.search !== '' || filteredCompanyId) {
      return 'No hay productos que coincidan con estos filtros.';
    }
    if (!canCreate) {
      return 'Todavía no hay clientes con contrato en tus bodegas, así que no hay catálogo que mostrar.';
    }
    return 'Todavía no hay productos. Crea el primero para poder recibir mercadería.';
  })();

  return (
    <div data-testid="products-page" className="space-y-6 animate-in fade-in duration-300">
      <PageHeader
        icon={Tag}
        title="Productos"
        description="Los SKU de tus clientes. Lo que se define acá manda en toda la bodega: si un producto controla lote, no se recibe sin indicarlo."
        actions={
          <>
            {/* In the header, not the filter strip: the strip has no room left at a laptop's width. */}
            {shopifyChannels.length > 0 && (
              <Button
                variant="ghost"
                icon={catalogSyncRunning ? SyncingIcon : RefreshCw}
                onClick={openShopifySync}
                data-testid="products-shopify-sync"
                data-running={String(catalogSyncRunning)}
              >
                {catalogSyncRunning ? 'Sincronizando SKUs…' : 'Sincronizar con Shopify'}
              </Button>
            )}
            <Button
              variant="primary"
              icon={Plus}
              disabled={!canCreate}
              title={
                canCreate ? undefined : 'No hay clientes dados de alta a los que cargarles productos'
              }
              onClick={() => setForm({ product: null })}
              data-testid="product-add"
            >
              Nuevo producto
            </Button>
          </>
        }
      />

      <FilterBar
        variant="bare"
        testId="products-filters"
        actions={
          <>
            <Button
              variant="ghost"
              icon={Upload}
              disabled={!canCreate}
              onClick={() => setIsUploadOpen(true)}
              data-testid="products-upload"
            >
              Carga masiva
            </Button>
            <Button
              variant="ghost"
              icon={Download}
              onClick={handleExport}
              disabled={products.length === 0}
              title={
                products.length > 0
                  ? 'Excel de los productos que estás viendo'
                  : 'No hay productos que exportar'
              }
              data-testid="products-export"
            >
              Exportar
            </Button>
          </>
        }
      >
        <SearchInput
          variant="bare"
          value={searchInput}
          onChange={setSearchInput}
          placeholder="Buscar por SKU, nombre o EAN..."
          testId="products-search"
        />

        {showClient && (
          <FilterableSelect
            label="Cliente:"
            value={filters.company}
            onChange={(company) => dispatch(setCatalogFilters({ company }))}
            options={[{ value: ALL_CLIENTS, label: 'Todos' }, ...clientOptions]}
            searchPlaceholder="Buscar cliente..."
            panelMinWidth={240}
            testId="products-client"
          />
        )}

        <Switch
          checked={filters.onlyActive}
          onChange={(onlyActive) => dispatch(setCatalogFilters({ onlyActive }))}
          label="Solo activos"
          testId="products-only-active"
        />
        {/* A filter, not the rule: a product with no balance here still exists
            to declare a receipt for. */}
        <Switch
          checked={filters.onlyWithStock}
          onChange={(onlyWithStock) => dispatch(setCatalogFilters({ onlyWithStock }))}
          label={warehouse ? `Solo con stock en ${warehouse.name}` : 'Solo con stock'}
          testId="products-only-with-stock"
        />
      </FilterBar>

      <DataTable
        columns={columns}
        data={products}
        totalItems={totalCount}
        currentPage={filters.page}
        itemsPerPage={filters.rowsPerPage}
        isLoading={loading}
        error={error}
        onRetry={() => dispatch(fetchCatalog())}
        itemLabel="productos"
        emptyMessage={emptyMessage}
        onPageChange={(page) => dispatch(setCatalogFilters({ page }))}
        onRowsPerPageChange={(rowsPerPage) => dispatch(setCatalogFilters({ rowsPerPage }))}
      />

      <ProductFormModal
        isOpen={Boolean(form)}
        onClose={() => setForm(null)}
        product={form?.product ?? null}
        clientOptions={clientOptions}
        defaultCompanyId={filteredCompanyId}
        onSaved={(message) => setNotice({ type: 'success', message })}
      />

      <CatalogUploadModal
        isOpen={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        clientOptions={clientOptions}
        defaultCompanyId={filteredCompanyId}
        onDone={(message) => setNotice({ type: 'success', message })}
      />

      <ShopifySkuSyncModal isOpen={isShopifyOpen} onClose={() => setIsShopifyOpen(false)} />

      <ConfirmDialog
        isOpen={Boolean(toRetire)}
        onClose={() => setToRetire(null)}
        onConfirm={() => toggleActive(toRetire)}
        title={toRetire ? `Dar de baja ${toRetire.sku}` : ''}
        message={
          toRetire
            ? `Todavía hay ${formatQuantity(toRetire.stock_total)} ${toRetire.base_uom} en bodega. Darlo de baja no las saca: conserva su stock y su historial, y deja de ofrecerse para recepciones y pedidos nuevos. Se puede reactivar.`
            : ''
        }
        confirmLabel="Dar de baja"
        loadingLabel="Dando de baja..."
        loading={Boolean(toRetire) && busyId === toRetire.id}
        testId="product-retire-dialog"
      />

    </div>
  );
};
